import { NetworkError } from './networkError';

type ApiErrorResponseInfo<TData = unknown> = {
  status: number;
  data: TData;
  headers: Headers;
};

export class ApiError<TData = unknown> extends Error {
  readonly response: ApiErrorResponseInfo<TData> | undefined;

  readonly status: number | undefined;

  constructor(
    message: string,
    options: {
      status?: number;
      data?: TData;
      headers?: Headers;
      cause?: unknown;
    } = {},
  ) {
    super(message, { cause: options.cause });
    this.name = 'ApiError';
    this.status = options.status;
    if (options.status !== undefined) {
      this.response = {
        status: options.status,
        data: options.data as TData,
        headers: options.headers ?? new Headers(),
      };
    }
  }
}

// Whether an error is unexpected. Unexpected = not an ApiError, a network
// failure (no response), or a 5xx. Expected client 4xx are handled at the call
// site (shown to the user).
//
// This drives TanStack's throwOnError (see plugins/vueQuery.ts) as well as
// logUnexpectedError, so it describes handled-ness, not reportability. For
// "should Sentry see this", use isReportableError.
export function isUnexpectedError(err: unknown): boolean {
  return !(err instanceof ApiError) || !err.response || err.response.status >= 500;
}

// Whether a failure is worth another attempt: a transport failure, a 429, or a
// server error that may not recur. Other 4xx are the caller's problem. Applied
// per call site, not as a TanStack default: retrying every mutation would
// duplicate non-idempotent writes.
export function isTransientError(err: unknown): boolean {
  if (err instanceof NetworkError) return true;
  if (!(err instanceof ApiError)) return false;
  // No status means the request never got a response.
  return err.status === undefined || err.status === 429 || err.status >= 500;
}

const MUTATING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

function isMutatingMethod(method: string | undefined): boolean {
  return method !== undefined && MUTATING_METHODS.includes(method.toUpperCase());
}

// Our API answers every 4xx with a JSON body, so an HTML one means something in
// front of it replied instead — an ALB or CloudFront error page, or a WAF block
// with no custom response of its own. Those are nobody's handled case.
//
// It also catches a non-4xx: fetchClient raises an ApiError when a 2xx body is
// HTML, which isUnexpectedError reads as handled.
//
// Matched on content-type first because a WAF block body is HTML but need not
// start with a doctype. String bodies that aren't HTML are left alone: legacy
// 401s still return a bare reason code (e.g. "ssoEnforced") as their body.
function isEdgeErrorBody({ data, headers }: ApiErrorResponseInfo): boolean {
  if (headers.get('content-type')?.toLowerCase().includes('text/html')) return true;
  return typeof data === 'string' && /^\s*<(?:!doctype\s+html|html)\b/i.test(data);
}

// WAF's custom response body on a block (infra/modules/waf/alb,
// custom_response_body "blocked_at_edge"). The block carries CORS headers, so
// it arrives as a readable 403 with nothing else to mark it as one.
const EDGE_BLOCK_REASON = 'blockedAtEdge';

// `reason` is what WAF sends; `reasonCode` is our own name for the same thing,
// accepted so aligning them later can't silently drop the signal again.
function isEdgeBlockBody(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  return (
    ('reason' in data && data.reason === EDGE_BLOCK_REASON) ||
    ('reasonCode' in data && data.reasonCode === EDGE_BLOCK_REASON)
  );
}

// Whether an error should reach Sentry. Wider than isUnexpectedError in three
// ways, none of which the call site handles — the gap that hid RL-8043, a WAF
// rule silently blocking discussion comments for a customer:
//
//  1. A transport failure on a mutating request. A dropped GET is dominated by
//     client-side network conditions and stays unreported, but a dropped
//     mutation loses user input. It is also the only trace left by a block that
//     answers without CORS headers, since the browser then rejects the fetch
//     and hides the status behind an opaque TypeError.
//  2. A body carrying the edge-block reason code (see isEdgeBlockBody).
//  3. A 4xx whose body did not come from our API (see isEdgeErrorBody).
export function isReportableError(err: unknown): boolean {
  if (err instanceof NetworkError) return isMutatingMethod(err.method);
  if (err instanceof ApiError && err.response) {
    if (isEdgeBlockBody(err.response.data)) return true;
    if (isEdgeErrorBody(err.response)) return true;
  }
  return isUnexpectedError(err);
}

// RFC 9457 Problem Details. https://www.rfc-editor.org/rfc/rfc9457
type ProblemDetails = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [extension: string]: unknown;
};

export type ApiTypedError<TReasonCode extends string = string> = ApiError<{
  code: number;
  message: string;
  userMessage?: string;
  reasonCode?: TReasonCode;
}> &
  // RFC 9457 members, populated during/after the backend migration. See
  // normaliseErrorBody for how these map onto the legacy names above.
  ProblemDetails;

// The trailing path/fragment segment of an RFC 9457 `type` URI is our
// historical bare-token reason code (e.g. ".../errors/mfa_code_mismatch" ->
// "mfa_code_mismatch"). `about:blank` and empty values carry no code.
function typeSlug(type: unknown): string | undefined {
  if (typeof type !== 'string' || type === '' || type === 'about:blank') return undefined;
  return type.split(/[#/]/).filter(Boolean).pop();
}

const asString = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const asNumber = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);

// Map an error body - the legacy `{ code, message, reasonCode, userMessage }`
// shape OR an RFC 9457 problem document - onto one that always exposes the
// legacy field names, so any downstream readers are unaffected
// Legacy fields win where present; problem fields fill the gaps
// Nothing is removed, YET
export function normaliseErrorBody(parsed: unknown): unknown {
  if (typeof parsed !== 'object' || parsed === null) return parsed;
  const p = parsed as ProblemDetails & Record<string, unknown>;

  const detail = asString(p.detail);
  const reasonCode = asString(p.reasonCode) ?? typeSlug(p.type);
  const message = asString(p.message) ?? detail ?? asString(p.title);
  const userMessage = asString(p.userMessage) ?? detail;
  const code = asNumber(p.code) ?? asNumber(p.status);

  return {
    ...p,
    ...(reasonCode !== undefined && { reasonCode }),
    ...(message !== undefined && { message }),
    ...(userMessage !== undefined && { userMessage }),
    ...(code !== undefined && { code }),
  };
}

export function getErrorMessage(err: ApiError | null): string | null {
  const data = err?.response?.data;
  return (
    (typeof data === 'object' &&
      data &&
      'message' in data &&
      typeof data.message === 'string' &&
      data.message) ||
    null
  );
}

// Reason code off an already-parsed body. Reads the legacy `reasonCode` and,
// once the body has passed through normaliseErrorBody, the RFC 9457 `type`
// slug (which normaliseErrorBody writes into `reasonCode`).
export function getReasonCodeFromData(data: unknown): string | null {
  if (typeof data === 'object' && data && 'reasonCode' in data) {
    const code = data.reasonCode;
    if (typeof code === 'string') return code;
  }
  return null;
}

// Semantic code for call sites that historically compared getErrorMessage()
// against a slug. Prefers the reason code (incl. the RFC 9457 type slug) and
// falls back to the message string, so a not-yet-migrated endpoint that still
// returns the slug as its message keeps matching.
export function getErrorCode(err: ApiError | null): string | null {
  return getReasonCodeFromData(err?.response?.data) ?? getErrorMessage(err);
}

// A problem's `errors[]` entries at "body.<field>", as { field: message }.
// Other locations, and chi bodies, give nothing.
export function getBodyFieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError)) return {};
  const data = err.response?.data;
  if (typeof data !== 'object' || data === null || !('errors' in data)) return {};
  const { errors } = data;
  if (!Array.isArray(errors)) return {};

  const fields: Record<string, string> = {};
  for (const entry of errors) {
    if (typeof entry !== 'object' || entry === null) continue;
    const location = asString((entry as Record<string, unknown>).location);
    const message = asString((entry as Record<string, unknown>).message);
    const field = location?.startsWith('body.') ? location.slice('body.'.length) : undefined;
    if (field && message !== undefined) fields[field] = message;
  }
  return fields;
}

// The API's message for the user on a client error, or null; never a 5xx's.
// A problem's `detail` arrives as `userMessage`, a chi body's is in `message`.
// A problem's own `message` is only its title, so it is ignored.
export function getUserMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || !err.response || err.response.status >= 500) return null;
  const { data } = err.response;
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;

  const userMessage = asString(body.userMessage);
  if (userMessage) return userMessage;
  if ('title' in body || 'detail' in body || 'type' in body) return null;
  return asString(body.message) || null;
}
